import { API_BASE_URL } from '../services/api';

// Helper function to make API requests with authentication
const makeRequest = async (method, endpoint, data = null) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = localStorage.getItem('token');
  
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    }
  };

  if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API ${method} ${endpoint} error:`, error);
    throw error;
  }
};

// Subscription API endpoints
const subscriptionAPI = {
  // Get user's subscriptions
  getUserSubscriptions: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/subscriptions${queryParams ? `?${queryParams}` : ''}`;
      return await makeRequest('GET', url);
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      throw error;
    }
  },

  // Get specific subscription details
  getSubscription: async (subscriptionId) => {
    try {
      return await makeRequest('GET', `/api/subscriptions/${subscriptionId}`);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  },

  // Calculate subscription pricing
  calculatePricing: async (subscriptionData) => {
    try {
      return await makeRequest('POST', '/api/subscriptions/calculate-pricing', subscriptionData);
    } catch (error) {
      console.error('Error calculating subscription pricing:', error);
      throw error;
    }
  },

  // Create new subscription
  createSubscription: async (subscriptionData) => {
    try {
      return await makeRequest('POST', '/api/subscriptions', subscriptionData);
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  },

  // Create payment order for subscription
  createPaymentOrder: async (subscriptionId) => {
    try {
      return await makeRequest('POST', `/api/subscriptions/${subscriptionId}/create-payment`);
    } catch (error) {
      console.error('Error creating payment order:', error);
      throw error;
    }
  },

  // Verify payment for subscription
  verifyPayment: async (subscriptionId, paymentData) => {
    try {
      return await makeRequest('POST', `/api/subscriptions/${subscriptionId}/verify-payment`, paymentData);
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  },

  // Pause subscription
  pauseSubscription: async (subscriptionId, pauseData) => {
    try {
      return await makeRequest('PUT', `/api/subscriptions/${subscriptionId}/pause`, pauseData);
    } catch (error) {
      console.error('Error pausing subscription:', error);
      throw error;
    }
  },

  // Resume subscription
  resumeSubscription: async (subscriptionId) => {
    try {
      return await makeRequest('PUT', `/api/subscriptions/${subscriptionId}/resume`);
    } catch (error) {
      console.error('Error resuming subscription:', error);
      throw error;
    }
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId, cancelData) => {
    try {
      return await makeRequest('PUT', `/api/subscriptions/${subscriptionId}/cancel`, cancelData);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  // Get helper's subscriptions (for helper dashboard)
  getHelperSubscriptions: async (helperId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/subscriptions/helper/${helperId}${queryParams ? `?${queryParams}` : ''}`;
      return await makeRequest('GET', url);
    } catch (error) {
      console.error('Error fetching helper subscriptions:', error);
      throw error;
    }
  },

  // Accept subscription (for helpers)
  acceptSubscription: async (subscriptionId) => {
    try {
      return await makeRequest('PUT', `/api/subscriptions/${subscriptionId}/accept`);
    } catch (error) {
      console.error('Error accepting subscription:', error);
      throw error;
    }
  },

  // Decline subscription (for helpers)
  declineSubscription: async (subscriptionId, declineData) => {
    try {
      return await makeRequest('PUT', `/api/subscriptions/${subscriptionId}/decline`, declineData);
    } catch (error) {
      console.error('Error declining subscription:', error);
      throw error;
    }
  },

  // Mark service complete (for helpers)
  markServiceComplete: async (subscriptionId, serviceData) => {
    try {
      return await makeRequest('POST', `/api/subscriptions/${subscriptionId}/mark-service-complete`, serviceData);
    } catch (error) {
      console.error('Error marking service complete:', error);
      throw error;
    }
  }
};

export default subscriptionAPI; 